import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SortIcon from "@mui/icons-material/Sort";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { toast } from "react-toastify";
import api from "../../conf/api";
import { FeedType } from "./Enums/enums";
import {
  PLATFORM_CATEGORIES,
  PLATFORM_EXTRA_CONFIG_SCHEMA,
  PLATFORMS_WITH_SETTINGS,
} from "./Enums/platform_constants";
import FeedSchedulerBox from "./components/FeedSchedulerBox";
import FeedLockDialog from "./components/FeedAuthenticationBox";

// ─── Colour tokens per feed type ─────────────────────────────────────────────
const TYPE_PALETTE = {
  [FeedType.SOCIAL]: { bg: "#3b82f6", light: "#eff6ff", text: "#1d4ed8" },
  [FeedType.PARTNER]: { bg: "#8b5cf6", light: "#f5f3ff", text: "#6d28d9" },
};

const LOGS_PAGE_SIZE = 10;

const PLATFORM_NAMES = {
  YOUTUBE: "YouTube", TIKTOK: "TikTok", FACEBOOK: "Facebook", INSTAGRAM: "Instagram",
  PINTEREST: "Pinterest", SNAPCHAT: "Snapchat", THREADS: "Threads",
  DAILYMOTION: "Dailymotion", MSN: "MSN", OTHERS: "Others",
};

// Hover content for the Pending chip: pending items per platform, most first
function PendingByPlatform({ counts }) {
  const rows = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return "No platform info";
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
        Pending by platform
      </Typography>
      {rows.map(([platform, n]) => (
        <Box key={platform} display="flex" justifyContent="space-between" gap={2}>
          <Typography variant="caption">{PLATFORM_NAMES[platform] || platform}</Typography>
          <Typography variant="caption" fontWeight={700}>{n}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Publish logs (latest first, 10 at a time with Load More) ────────────────
function PublishLogsSection({ logs }) {
  const [visibleCount, setVisibleCount] = useState(LOGS_PAGE_SIZE);
  const ordered = [...logs].reverse();
  const visible = ordered.slice(0, visibleCount);
  const remaining = ordered.length - visibleCount;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mt: 1.5,
        border: "1px solid #e2e8f0",
        borderRadius: "8px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: "#f8fafc",
          minHeight: 40,
          "&.Mui-expanded": { minHeight: 40 },
          "& .MuiAccordionSummary-content": { my: "8px !important" },
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          PUBLISH LOGS ({ordered.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["Date", "Platform", "Action", "Reason"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((log, i) => (
                <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption">
                      {log.created_date
                        ? new Date(log.created_date).toLocaleString()
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {log.platform?.toLowerCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action}
                      size="small"
                      color={
                        log.action === "PUBLISHED"
                          ? "success"
                          : log.action === "FAILED"
                            ? "error"
                            : log.action === "SKIPPED"
                              ? "warning"
                              : "default"
                      }
                      sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {log.reason || "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {remaining > 0 && (
          <Box
            display="flex"
            justifyContent="center"
            py={1}
            sx={{ borderTop: "1px solid #f1f5f9" }}
          >
            <Button
              size="small"
              onClick={() => setVisibleCount((c) => c + LOGS_PAGE_SIZE)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 12 }}
            >
              Load More ({remaining} remaining)
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function FeedsPreview() {
  const [feeds, setFeeds] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subbrands, setSubbrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [openSchedulerDialog, setOpenSchedulerDialog] = useState(false);
  const [selectedFeedForScheduler, setSelectedFeedForScheduler] =
    useState(null);
  const [openItemsDialog, setOpenItemsDialog] = useState(false);
  const [selectedFeedForItems, setSelectedFeedForItems] = useState(null);
  const [feedItems, setFeedItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsCount, setItemsCount] = useState(0);
  const [itemsSearchInput, setItemsSearchInput] = useState("");
  const [itemsSearchText, setItemsSearchText] = useState("");
  const itemsSearchDebounceRef = useRef(null);
  const ITEMS_PAGE_SIZE = 10;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [openLockDialog, setOpenLockDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedToDelete, setFeedToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Feed item delete state
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [feedItemToDelete, setFeedItemToDelete] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  // Filter & sort state
  const [filterBrand, setFilterBrand] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ordering, setOrdering] = useState("name");
  const searchDebounceRef = useRef(null);

  // platformConfigs: { [platform]: { category: string, extra_config: {} } }
  const [platformConfigs, setPlatformConfigs] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    feed_type: FeedType.SOCIAL,
    brand: null,
    subbrand: null,
    title: "",
    description: "",
    link: "",
    xml_url: "",
    is_active: true,
  });

  useEffect(() => {
    fetchFeeds();
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    filterBrand,
    searchText,
    ordering,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setSearchText(value);
    }, 400);
  };

  const handleFilterBrandChange = (value) => {
    setFilterBrand(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOrderingChange = (value) => {
    setOrdering(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchText("");
    setFilterBrand("");
    setOrdering("name");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = searchText || filterBrand || ordering !== "name";

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        ordering,
      };
      if (filterBrand) params.brand = filterBrand;
      if (searchText) params.search = searchText;
      const response = await api.get("/mrss/feeds/", { params });
      setFeeds(response.data.results || response.data);
      setRowCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching feeds:", error);
      toast.error("Failed to load feeds");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/mrss/brands/?page_size=1000");
      setBrands(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchSubbrands = async (brandName) => {
    try {
      const response = await api.get("/mrss/subbrands/?page_size=1000", {
        params: { brand: brandName },
      });
      setSubbrands(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching subbrands:", error);
    }
  };

  const handleBrandChange = (brandName) => {
    setFormData({ ...formData, brand: brandName, subbrand: null });
    if (brandName) {
      fetchSubbrands(brandName);
      generateXmlUrl(formData.name, brandName, null);
    } else {
      setSubbrands([]);
    }
  };

  const generateXmlUrl = (feedName, brandName, subbrandId) => {
    if (!feedName) return;
    const selectedBrand = brands.find((b) => b.name === brandName);
    const selectedSubbrand = subbrands.find((s) => s.id === subbrandId);
    let languageName = "";
    if (selectedSubbrand?.language)
      languageName = selectedSubbrand.language.name;
    else if (selectedBrand?.default_language)
      languageName = selectedBrand.default_language.name;
    let slug = feedName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (languageName && languageName.toLowerCase() !== "english") {
      const langSlug = languageName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      slug = `${slug}-${langSlug}`;
    }
    setFormData((prev) => ({ ...prev, xml_url: slug }));
  };

  const fetchPlatformConfigs = async (feedId) => {
    try {
      const response = await api.get("/mrss/feed-platform-configs/", {
        params: { feed: feedId },
      });
      const configs = {};
      (response.data.results || response.data).forEach((cfg) => {
        configs[cfg.platform] = {
          category: cfg.category || "",
          extra_config: cfg.extra_config || {},
        };
      });
      setPlatformConfigs(configs);
    } catch (error) {
      console.error("Error fetching platform configs:", error);
      setPlatformConfigs({});
    }
  };

  const handleOpenDialog = (feed = null) => {
    setDialogTab(0);
    if (feed) {
      setEditingFeed(feed);
      setFormData({
        name: feed.name,
        feed_type: feed.feed_type,
        brand: feed.brand,
        subbrand: feed.subbrand,
        title: feed.title,
        description: feed.description || "",
        link: feed.link || "",
        xml_url: feed.xml_url,
        is_active: feed.is_active,
      });
      if (feed.brand) fetchSubbrands(feed.brand_name);
      fetchPlatformConfigs(feed.id);
    } else {
      setEditingFeed(null);
      setFormData({
        name: "",
        feed_type: FeedType.SOCIAL,
        brand: null,
        subbrand: null,
        title: "",
        description: "",
        link: "",
        xml_url: "",
        is_active: true,
      });
      setPlatformConfigs({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFeed(null);
    setSubbrands([]);
    setPlatformConfigs({});
    setDialogTab(0);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim() || !formData.title.trim()) {
        toast.warning("Feed name and title are required");
        return;
      }
      if (!formData.brand && !formData.subbrand) {
        toast.warning("Feed must belong to either a brand or subbrand");
        return;
      }
      let savedFeed;
      if (editingFeed) {
        const resp = await api.put(`/mrss/feeds/${editingFeed.id}/`, formData);
        savedFeed = resp.data;
        toast.success("Feed updated successfully");
      } else {
        const resp = await api.post("/mrss/feeds/", formData);
        savedFeed = resp.data;
        toast.success("Feed created successfully");
      }
      const configs = PLATFORMS_WITH_SETTINGS.map((platform) => ({
        platform,
        category: platformConfigs[platform]?.category || "",
        extra_config: platformConfigs[platform]?.extra_config || {},
      })).filter((c) => c.category || Object.keys(c.extra_config).length > 0);
      await api.post("/mrss/feed-platform-configs/bulk_upsert/", {
        feed: savedFeed.id,
        configs,
      });
      handleCloseDialog();
      fetchFeeds();
    } catch (error) {
      console.error("Error saving feed:", error);
      toast.error(error.response?.data?.error || "Failed to save feed");
    }
  };

  const handleDeleteClick = (feed) => {
    setFeedToDelete(feed);
    setDeleteConfirmText("");
    setDeleteDialogOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFeedToDelete(null);
    setDeleteConfirmText("");
  };
  const handleConfirmDelete = async () => {
    if (!feedToDelete) return;
    try {
      await api.delete(`/mrss/feeds/${feedToDelete.id}/`);
      toast.success("Feed deleted successfully");
      handleCloseDeleteDialog();
      fetchFeeds();
    } catch (error) {
      console.error("Error deleting feed:", error);
      toast.error("Failed to delete feed");
    }
  };

  const handleOpenScheduler = (feed) => {
    setSelectedFeedForScheduler(feed);
    setOpenSchedulerDialog(true);
  };
  const handleCloseScheduler = () => {
    setOpenSchedulerDialog(false);
    setSelectedFeedForScheduler(null);
    fetchFeeds();
  };

  const fetchFeedItems = async (feedId, page, search) => {
    try {
      setLoadingItems(true);
      const params = { feed: feedId, page, page_size: ITEMS_PAGE_SIZE };
      if (search) params.search = search;
      const response = await api.get("/mrss/feed-items/", { params });
      setFeedItems(response.data.results || response.data);
      setItemsCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching feed items:", error);
      toast.error("Failed to load feed items");
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    if (openItemsDialog && selectedFeedForItems) {
      fetchFeedItems(selectedFeedForItems.id, itemsPage, itemsSearchText);
    }
  }, [openItemsDialog, selectedFeedForItems?.id, itemsPage, itemsSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemsSearchChange = (value) => {
    setItemsSearchInput(value);
    clearTimeout(itemsSearchDebounceRef.current);
    itemsSearchDebounceRef.current = setTimeout(() => {
      setItemsPage(1);
      setItemsSearchText(value);
    }, 400);
  };

  const handleOpenItems = (feed) => {
    setSelectedFeedForItems(feed);
    setItemsPage(1);
    setItemsCount(0);
    setItemsSearchInput("");
    setItemsSearchText("");
    setFeedItems([]);
    setOpenItemsDialog(true);
  };
  const handleCloseItems = () => {
    clearTimeout(itemsSearchDebounceRef.current);
    setOpenItemsDialog(false);
    setSelectedFeedForItems(null);
    setFeedItems([]);
    setItemsPage(1);
    setItemsCount(0);
    setItemsSearchInput("");
    setItemsSearchText("");
  };

  // Feed item delete handlers
  const handleDeleteItemClick = (item) => {
    setFeedItemToDelete(item);
    setDeleteItemDialogOpen(true);
  };
  const handleCloseDeleteItemDialog = () => {
    setDeleteItemDialogOpen(false);
    setFeedItemToDelete(null);
  };
  const handleConfirmDeleteItem = async () => {
    if (!feedItemToDelete) return;
    setDeletingItem(true);
    try {
      await api.delete(`/mrss/feed-items/${feedItemToDelete.id}/`);
      toast.success("Feed item deleted successfully");
      handleCloseDeleteItemDialog();
      // Refresh the feed items list
      if (selectedFeedForItems) {
        fetchFeedItems(selectedFeedForItems.id, itemsPage, itemsSearchText);
      }
      // Also refresh feeds to update item counts
      fetchFeeds();
    } catch (error) {
      console.error("Error deleting feed item:", error);
      toast.error(error.response?.data?.detail || "Failed to delete feed item");
    } finally {
      setDeletingItem(false);
    }
  };
  const handleAuthenticationBox = (feed) => {
    setSelectedFeedForItems(feed);
    setOpenLockDialog(true);
  };

  const FeedDialogBox = useCallback(
    () => (
      <FeedLockDialog
        open={openLockDialog}
        setOpen={setOpenLockDialog}
        feed={selectedFeedForItems}
        onSuccess={fetchFeeds}
      />
    ),
    [selectedFeedForItems?.id, openLockDialog],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const getPlatformIcon = (platform) => {
    const p = platform.toLowerCase();
    if (p.includes("facebook")) return <FacebookIcon fontSize="small" />;
    if (p.includes("instagram")) return <InstagramIcon fontSize="small" />;
    if (p.includes("twitter") || p.includes("x"))
      return <TwitterIcon fontSize="small" />;
    if (p.includes("youtube")) return <YouTubeIcon fontSize="small" />;
    return null;
  };

  // ─── Stats derived from loaded page ────────────────────────────────────────
  const activeCount = feeds.filter((f) => f.is_active).length;
  const totalItems = feeds.reduce((s, f) => s + (f.items_count || 0), 0);
  const totalPending = feeds.reduce((s, f) => s + (f.pending_count || 0), 0);

  // ─── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      field: "name",
      headerName: "Feed",
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => {
        const palette =
          TYPE_PALETTE[params.row.feed_type] || TYPE_PALETTE[FeedType.SOCIAL];
        return (
          <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: palette.bg,
                flexShrink: 0,
                borderRadius: 1.5,
              }}
            >
              <RssFeedIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {params.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {params.row.title}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "feed_type",
      headerName: "Type",
      width: 110,
      renderCell: (params) => {
        const palette =
          TYPE_PALETTE[params.value] || TYPE_PALETTE[FeedType.SOCIAL];
        const Icon =
          params.value === FeedType.SOCIAL ? PodcastsIcon : HandshakeIcon;
        return (
          <Chip
            icon={
              <Icon
                sx={{
                  fontSize: "14px !important",
                  color: `${palette.text} !important`,
                }}
              />
            }
            label={params.value}
            size="small"
            sx={{
              bgcolor: palette.light,
              color: palette.text,
              fontWeight: 600,
              fontSize: 11,
              border: `1px solid ${alpha(palette.bg, 0.25)}`,
              "& .MuiChip-icon": { color: palette.text },
            }}
          />
        );
      },
    },
    {
      field: "owner_name",
      headerName: "Owner",
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "capitalize" }}
          >
            {params.row.owner_type?.toLowerCase()}
          </Typography>
        </Box>
      ),
    },
    {
      field: "items_count",
      headerName: "Items",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: alpha("#10b981", 0.1),
              color: "#059669",
              fontWeight: 700,
              fontSize: 12,
              border: "1px solid " + alpha("#10b981", 0.3),
            }}
          />
        </Box>
      ),
    },
    {
      field: "pending_count",
      headerName: "Pending",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Tooltip
            arrow
            title={params.value > 0 ? <PendingByPlatform counts={params.row.pending_by_platform} /> : ""}
          >
            <Chip
              label={params.value}
              size="small"
              icon={
                params.value > 0 ? (
                  <HourglassEmptyIcon sx={{ fontSize: "12px !important" }} />
                ) : undefined
              }
              sx={{
                cursor: params.value > 0 ? "help" : "default",
                bgcolor:
                  params.value > 0
                    ? alpha("#f59e0b", 0.1)
                    : alpha("#6b7280", 0.08),
                color: params.value > 0 ? "#d97706" : "#9ca3af",
                fontWeight: 700,
                fontSize: 12,
                border:
                  "1px solid " +
                  (params.value > 0
                    ? alpha("#f59e0b", 0.3)
                    : alpha("#6b7280", 0.2)),
              }}
            />
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "xml_url",
      headerName: "XML URL",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const apiBase = process.env.REACT_APP_BASE_URL || "";
        let xmlUrlPath = (params.value || "").replace(/^\/+|\/+$/g, "");
        if (xmlUrlPath.includes("mrss/feed/"))
          xmlUrlPath = xmlUrlPath.split("mrss/feed/").pop();
        const feedUrl = `${apiBase}/mrss/feed/${xmlUrlPath}/`;
        return (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ maxWidth: "100%" }}
          >
            <Link
              href={feedUrl}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
                color: "primary.main",
              }}
            >
              <LinkIcon sx={{ fontSize: 14, flexShrink: 0 }} />
              <Typography
                variant="caption"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 11 }}
              >
                {xmlUrlPath}
              </Typography>
            </Link>
            <Tooltip title="Copy URL">
              <IconButton
                size="small"
                sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1 } }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(feedUrl);
                  toast.info("URL copied");
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center">
          {params.value ? (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                    ml: "6px !important",
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%,100%": { opacity: 1 },
                      "50%": { opacity: 0.4 },
                    },
                  }}
                />
              }
              label="Active"
              size="small"
              sx={{
                bgcolor: alpha("#10b981", 0.1),
                color: "#059669",
                fontWeight: 600,
                fontSize: 11,
                border: "1px solid " + alpha("#10b981", 0.3),
              }}
            />
          ) : (
            <Chip
              label="Inactive"
              size="small"
              sx={{
                bgcolor: alpha("#6b7280", 0.08),
                color: "#9ca3af",
                fontWeight: 600,
                fontSize: 11,
                border: "1px solid " + alpha("#6b7280", 0.2),
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          gap={0.25}
          justifyContent="flex-end"
        >
          <Tooltip
            title={
              params.row.is_private
                ? "Locked — click to manage token"
                : "Unlocked — click to lock"
            }
          >
            <IconButton
              size="small"
              onClick={() => handleAuthenticationBox(params.row)}
              sx={{
                p: 0.75,
                borderRadius: 1,
                "&:hover": { bgcolor: alpha("#0ea5e9", 0.1) },
              }}
            >
              {params.row.is_private ? (
                <LockIcon sx={{ fontSize: 16, color: "#ef4444" }} />
              ) : (
                <LockOpenIcon sx={{ fontSize: 16, color: "#10b981" }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="View feed items">
            <IconButton
              size="small"
              onClick={() => handleOpenItems(params.row)}
              sx={{
                p: 0.75,
                borderRadius: 1,
                "&:hover": { bgcolor: alpha("#0ea5e9", 0.1) },
              }}
            >
              <VisibilityIcon sx={{ fontSize: 16, color: "#0ea5e9" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit feed">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{
                p: 0.75,
                borderRadius: 1,
                "&:hover": { bgcolor: alpha("#3b82f6", 0.1) },
              }}
            >
              <EditIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configure schedule">
            <IconButton
              size="small"
              onClick={() => handleOpenScheduler(params.row)}
              sx={{
                p: 0.75,
                borderRadius: 1,
                "&:hover": { bgcolor: alpha("#10b981", 0.1) },
              }}
            >
              <ScheduleIcon sx={{ fontSize: 16, color: "#10b981" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete feed">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              sx={{
                p: 0.75,
                borderRadius: 1,
                "&:hover": { bgcolor: alpha("#ef4444", 0.1) },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16, color: "#ef4444" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ─── Stat card ───────────────────────────────────────────────────────────────
  const StatCard = ({ label, value, icon, color, loading: cardLoading }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        flex: 1,
        minWidth: 130,
        borderColor: alpha(color, 0.25),
        background: `linear-gradient(135deg, ${alpha(color, 0.06)} 0%, transparent 100%)`,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: alpha(color, 0.12),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        {cardLoading ? (
          <Skeleton width={40} height={28} />
        ) : (
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* ── Hero Header ─────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background:
            "linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #0ea5e9 100%)",
          color: "white",
        }}
      >
        {/* decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.06)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            right: 80,
            width: 150,
            height: 150,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.04)",
            pointerEvents: "none",
          }}
        />

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <RssFeedIcon sx={{ fontSize: 28, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight={700} letterSpacing={-0.5}>
                Feed Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.75, pl: 0.5 }}>
              {rowCount} feed{rowCount !== 1 ? "s" : ""} total
              {hasActiveFilters ? " · filtered" : ""}
            </Typography>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchFeeds}
              disabled={loading}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: "white",
                color: "#1e40af",
                fontWeight: 700,
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                boxShadow: "none",
              }}
            >
              Create Feed
            </Button>
          </Box>
        </Box>

        {/* Stats row */}
        <Box display="flex" gap={1.5} mt={2.5} flexWrap="wrap">
          {[
            {
              label: "Total Feeds",
              value: rowCount,
              icon: <RssFeedIcon />,
              color: "#fff",
            },
            {
              label: "Active (page)",
              value: activeCount,
              icon: <CheckCircleIcon />,
              color: "#86efac",
            },
            {
              label: "Items (page)",
              value: totalItems,
              icon: <PodcastsIcon />,
              color: "#93c5fd",
            },
            {
              label: "Pending (page)",
              value: totalPending,
              icon: <HourglassEmptyIcon />,
              color: "#fde68a",
            },
          ].map(({ label, value, icon, color }) => (
            <Box
              key={label}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: "1 1 auto",
                minWidth: 110,
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, display: "block", lineHeight: 1 }}
                >
                  {label}
                </Typography>
                {loading ? (
                  <Skeleton
                    width={30}
                    height={22}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                  />
                ) : (
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    lineHeight={1.2}
                  >
                    {value}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ── Filter Toolbar ───────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: "white",
          borderColor: "#e2e8f0",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <FilterListIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.secondary"
          >
            Filters & Sorting
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Filters active"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: "auto", height: 22, fontSize: 11 }}
            />
          )}
        </Box>

        <Grid container spacing={1.5} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or title…"
              value={searchInput}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  "&:hover fieldset": { borderColor: "#3b82f6" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleSearchInputChange("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          {/* Brand */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select
                label="Brand"
                value={filterBrand}
                onChange={(e) => handleFilterBrandChange(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
              >
                <MenuItem value="">
                  <em>All Brands</em>
                </MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.name} value={b.name}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sort */}
          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <SortIcon sx={{ fontSize: 14 }} /> Sort By
                </Box>
              </InputLabel>
              <Select
                label="Sort By x"
                value={ordering}
                onChange={(e) => handleOrderingChange(e.target.value)}
                sx={{
                  borderRadius: 2,
                  bgcolor:
                    ordering !== "name" ? alpha("#3b82f6", 0.04) : "#f8fafc",
                }}
              >
                <MenuItem value="name">Name A → Z</MenuItem>
                <MenuItem value="-name">Name Z → A</MenuItem>
                <MenuItem value="items_count">Most Items</MenuItem>
                <MenuItem value="-items_count">Fewest Items</MenuItem>
                <MenuItem value="pending_count">Most Pending</MenuItem>
                <MenuItem value="-pending_count">Fewest Pending</MenuItem>
                <MenuItem value="feed_type">Partner First</MenuItem>
                <MenuItem value="-feed_type">Social First</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear */}
          <Grid item xs={12} sm="auto" md={2}>
            <Button
              fullWidth
              size="small"
              variant={hasActiveFilters ? "contained" : "outlined"}
              color={hasActiveFilters ? "warning" : "inherit"}
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              sx={{
                height: 40,
                borderRadius: 2,
                fontWeight: 600,
                ...(hasActiveFilters
                  ? {}
                  : { color: "text.disabled", borderColor: "#e2e8f0" }),
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Active filter chips */}
        {hasActiveFilters && (
          <Box
            display="flex"
            gap={0.75}
            flexWrap="wrap"
            mt={1.5}
            pt={1.5}
            sx={{ borderTop: "1px dashed #e2e8f0" }}
          >
            {filterBrand && (
              <Chip
                label={`Brand: ${filterBrand}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => handleFilterBrandChange("")}
                sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
              />
            )}
            {searchText && (
              <Chip
                label={`"${searchText}"`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => handleSearchInputChange("")}
                sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
              />
            )}
            {ordering !== "name" && (
              <Chip
                label={`Sort: ${ordering.replace("-", "↓ ").replace(/([A-Z])/g, " $1")}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => handleOrderingChange("name")}
                sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* ── Data Grid ────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "white",
          borderColor: "#e2e8f0",
        }}
      >
        <DataGrid
          rows={feeds}
          columns={columns}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={rowCount}
          disableSelectionOnClick
          rowHeight={60}
          sx={{
            border: "none",
            fontFamily: "inherit",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: 12,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              },
            },
            "& .MuiDataGrid-row": {
              "&:hover": { bgcolor: "#f0f9ff" },
              "&.Mui-selected": {
                bgcolor: alpha("#3b82f6", 0.06),
                "&:hover": { bgcolor: alpha("#3b82f6", 0.1) },
              },
              borderBottom: "1px solid #f1f5f9",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              alignItems: "center",
              display: "flex",
            },
            "& .MuiDataGrid-footerContainer": {
              bgcolor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { minHeight: 300 },
          }}
        />
      </Paper>

      {/* ── Create / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: editingFeed
                ? "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)"
                : "linear-gradient(135deg, #065f46 0%, #10b981 100%)",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {editingFeed ? `Editing: ${editingFeed.name}` : "Create New Feed"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {editingFeed
                ? "Update feed details and platform settings"
                : "Set up a new MRSS feed for distribution"}
            </Typography>
          </Box>
          <Tabs
            value={dialogTab}
            onChange={(_, v) => setDialogTab(v)}
            sx={{
              px: 3,
              bgcolor: "white",
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                minHeight: 44,
              },
              "& .Mui-selected": { color: "#3b82f6 !important" },
              "& .MuiTabs-indicator": {
                bgcolor: "#3b82f6",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            <Tab label="General" />
            <Tab label="Platform Settings" />
          </Tabs>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {dialogTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Feed Name"
                    value={formData.name}
                    required
                    onChange={(e) => {
                      const n = e.target.value;
                      setFormData({ ...formData, name: n });
                      generateXmlUrl(n, formData.brand, formData.subbrand);
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Feed Type</InputLabel>
                    <Select
                      value={formData.feed_type}
                      label="Feed Type"
                      onChange={(e) =>
                        setFormData({ ...formData, feed_type: e.target.value })
                      }
                      sx={{ borderRadius: 1.5 }}
                    >
                      <MenuItem value={FeedType.SOCIAL}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PodcastsIcon fontSize="small" color="primary" />{" "}
                          Social
                        </Box>
                      </MenuItem>
                      <MenuItem value={FeedType.PARTNER}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <HandshakeIcon fontSize="small" color="secondary" />{" "}
                          Partner
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={brands}
                    getOptionLabel={(o) => o.name}
                    value={
                      brands.find((b) => b.name === formData.brand) || null
                    }
                    onChange={(_, v) => handleBrandChange(v?.name || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Brand"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={subbrands}
                    getOptionLabel={(o) => o.name}
                    value={
                      subbrands.find((s) => s.id === formData.subbrand) || null
                    }
                    onChange={(_, v) => {
                      const id = v?.id || null;
                      setFormData({ ...formData, subbrand: id });
                      generateXmlUrl(formData.name, formData.brand, id);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subbrand (Optional)"
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                        }}
                      />
                    )}
                    disabled={!formData.brand}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Feed Title"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Link URL"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="XML URL Path"
                    required
                    value={formData.xml_url}
                    onChange={(e) =>
                      setFormData({ ...formData, xml_url: e.target.value })
                    }
                    helperText={
                      formData.xml_url
                        ? `${process.env.REACT_APP_BASE_URL || ""}/mrss/feed/${formData.xml_url}/`
                        : "Auto-generated (editable)"
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="caption" color="text.secondary">
                            /mrss/feed/
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Active Status
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.is_active
                          ? "Feed is live and accessible"
                          : "Feed is paused and hidden"}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                      color="success"
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {dialogTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                Category and options set here are embedded into the MRSS feed
                XML for each platform.
              </Alert>
              {PLATFORMS_WITH_SETTINGS.map((platform) => {
                const categories = PLATFORM_CATEGORIES[platform] || [];
                const extraSchema =
                  PLATFORM_EXTRA_CONFIG_SCHEMA[platform] || {};
                const cfg = platformConfigs[platform] || {};
                const extraCfg = cfg.extra_config || {};
                const updateConfig = (patch) =>
                  setPlatformConfigs((prev) => ({
                    ...prev,
                    [platform]: {
                      category: prev[platform]?.category || "",
                      extra_config: prev[platform]?.extra_config || {},
                      ...prev[platform],
                      ...patch,
                    },
                  }));
                const updateExtra = (field, value) =>
                  updateConfig({
                    extra_config: { ...extraCfg, [field]: value },
                  });
                return (
                  <Paper
                    key={platform}
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        bgcolor: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {platform}
                      </Typography>
                      {cfg.category && (
                        <Chip
                          label={cfg.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {categories.length > 0 && (
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Channel / Category</InputLabel>
                              <Select
                                label="Channel / Category"
                                value={cfg.category || ""}
                                onChange={(e) =>
                                  updateConfig({ category: e.target.value })
                                }
                                sx={{ borderRadius: 1.5 }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {categories.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        {Object.entries(extraSchema).map(([field, schema]) => (
                          <Grid item xs={12} sm={6} key={field}>
                            {schema.type === "bool" ? (
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    color="primary"
                                    checked={
                                      field in extraCfg
                                        ? Boolean(extraCfg[field])
                                        : schema.default
                                    }
                                    onChange={(e) =>
                                      updateExtra(field, e.target.checked)
                                    }
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {schema.label}
                                  </Typography>
                                }
                              />
                            ) : schema.type === "select" ? (
                              <FormControl fullWidth size="small">
                                <InputLabel>{schema.label}</InputLabel>
                                <Select
                                  label={schema.label}
                                  value={extraCfg[field] ?? schema.default}
                                  onChange={(e) =>
                                    updateExtra(field, e.target.value)
                                  }
                                  sx={{ borderRadius: 1.5 }}
                                >
                                  {schema.options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : schema.type === "number" ? (
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={schema.label}
                                value={extraCfg[field] ?? schema.default ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const n = raw === "" ? null : Number(raw);
                                  updateExtra(
                                    field,
                                    n !== null && Number.isNaN(n) ? null : n,
                                  );
                                }}
                                inputProps={{ min: schema.min ?? 0, step: 1 }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1.5,
                                  },
                                }}
                              />
                            ) : null}
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 700,
              background: editingFeed
                ? "linear-gradient(135deg, #1e40af, #3b82f6)"
                : "linear-gradient(135deg, #065f46, #10b981)",
            }}
          >
            {editingFeed ? "Save Changes" : "Create Feed"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feed Scheduler ───────────────────────────────────────────────────── */}
      {openSchedulerDialog && selectedFeedForScheduler && (
        <FeedSchedulerBox
          open={openSchedulerDialog}
          selectedFeed={selectedFeedForScheduler}
          onClose={handleCloseScheduler}
        />
      )}

      {/* ── Feed Items Dialog ────────────────────────────────────────────────── */}
      <Dialog
        open={openItemsDialog}
        onClose={handleCloseItems}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "linear-gradient(135deg, #374151 0%, #6b7280 100%)",
              color: "white",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                }}
              >
                <VisibilityIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedFeedForItems?.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {selectedFeedForItems?.title}
                  {itemsCount > 0
                    ? ` · ${itemsCount} item${itemsCount !== 1 ? "s" : ""}`
                    : ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
          {/* Search bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by title, description or WGID…"
            value={itemsSearchInput}
            onChange={(e) => handleItemsSearchChange(e.target.value)}
            sx={{
              mt: 2,
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "white",
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: itemsSearchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleItemsSearchChange("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {loadingItems ? (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={120}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : feedItems.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              gap={1.5}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "#e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {itemsSearchText ? (
                  <SearchIcon sx={{ fontSize: 32, color: "#94a3b8" }} />
                ) : (
                  <RssFeedIcon sx={{ fontSize: 32, color: "#94a3b8" }} />
                )}
              </Box>
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                {itemsSearchText ? "No matching items" : "No items yet"}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {itemsSearchText
                  ? `Nothing matches "${itemsSearchText}". Try a different title, description or WGID.`
                  : "Nothing has been published to this feed."}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {feedItems.map((item) => (
                <Paper
                  key={item.id}
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    borderColor: "#e2e8f0",
                    position: "relative",
                  }}
                >
                  {/* Delete button */}
                  <Tooltip title="Delete this feed item">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteItemClick(item)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#ef4444",
                        bgcolor: "rgba(239,68,68,0.06)",
                        "&:hover": { bgcolor: "rgba(239,68,68,0.15)" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        gutterBottom
                      >
                        {item.display_title}
                      </Typography>
                      <Box display="flex" gap={2} mb={1} flexWrap="wrap">
                        {item.video_unique_key && (
                          <Typography variant="caption" color="text.secondary">
                            Key:{" "}
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                              }}
                            >
                              {item.video_unique_key}
                            </span>
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          GUID:{" "}
                          <span style={{ fontFamily: "monospace" }}>
                            {item.guid?.substring(0, 20)}…
                          </span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Published:{" "}
                          {new Date(item.published_date).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.display_description?.substring(0, 180)}
                        {item.display_description?.length > 180 ? "…" : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        display="block"
                        mb={1}
                      >
                        SOCIAL POSTS ({item.social_media_posts_count || 0})
                      </Typography>
                      {item.social_media_posts?.length > 0 ? (
                        item.social_media_posts.map((post) => (
                          <Box
                            key={post.id}
                            sx={{
                              p: 1,
                              mb: 0.75,
                              bgcolor: "#f8fafc",
                              borderRadius: 1.5,
                              border: "1px solid #e2e8f0",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ color: "#64748b" }}>
                              {getPlatformIcon(post.platform)}
                            </Box>
                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                display="block"
                                sx={{ textTransform: "capitalize" }}
                              >
                                {post.platform}
                              </Typography>
                              {post.post_url ? (
                                <Link
                                  href={post.post_url}
                                  target="_blank"
                                  rel="noopener"
                                  variant="caption"
                                >
                                  View Post
                                </Link>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  {post.platform_post_id}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f8fafc",
                            borderRadius: 1.5,
                            border: "1px dashed #e2e8f0",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.disabled">
                            Not posted yet
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {item.publish_logs?.length > 0 && (
                    <PublishLogsSection
                      key={`logs-${item.id}`}
                      logs={item.publish_logs}
                    />
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {/* Pagination */}
          {!loadingItems && itemsCount > ITEMS_PAGE_SIZE && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={2.5}
              gap={1.5}
            >
              <Pagination
                count={Math.ceil(itemsCount / ITEMS_PAGE_SIZE)}
                page={itemsPage}
                onChange={(_, page) => setItemsPage(page)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
              <Typography variant="caption" color="text.secondary">
                {(itemsPage - 1) * ITEMS_PAGE_SIZE + 1}–
                {Math.min(itemsPage * ITEMS_PAGE_SIZE, itemsCount)} of{" "}
                {itemsCount}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={handleCloseItems}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Feed Auth Dialog ─────────────────────────────────────────────────── */}
      <FeedDialogBox />

      {/* ── Delete Feed Item Confirmation ────────────────────────────────────────────── */}
      <Dialog
        open={deleteItemDialogOpen}
        onClose={handleCloseDeleteItemDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38 }}
            >
              <WarningAmberIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Delete Feed Item
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                This will remove the video from the feed
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {feedItemToDelete && (
            <>
              {/* Item details */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  borderColor: "#fca5a5",
                  bgcolor: "#fff5f5",
                }}
              >
                <Typography variant="body2" fontWeight={700} gutterBottom>
                  {feedItemToDelete.display_title}
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap" mb={0.5}>
                  {feedItemToDelete.video_unique_key && (
                    <Typography variant="caption" color="text.secondary">
                      Key:{" "}
                      <span
                        style={{ fontFamily: "monospace", fontWeight: 700 }}
                      >
                        {feedItemToDelete.video_unique_key}
                      </span>
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Published:{" "}
                    {new Date(feedItemToDelete.published_date).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>

              {/* Published platforms */}
              <Typography
                variant="subtitle2"
                fontWeight={700}
                gutterBottom
                sx={{ mt: 2 }}
              >
                Published Platforms
              </Typography>
              {feedItemToDelete.social_media_posts?.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={0.75} mb={2}>
                  {feedItemToDelete.social_media_posts.map((post) => (
                    <Box
                      key={post.id}
                      sx={{
                        p: 1.25,
                        bgcolor: "#f8fafc",
                        borderRadius: 1.5,
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ color: "#64748b" }}>
                        {getPlatformIcon(post.platform)}
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          display="block"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {post.platform}
                        </Typography>
                        {post.post_url ? (
                          <Link
                            href={post.post_url}
                            target="_blank"
                            rel="noopener"
                            variant="caption"
                          >
                            View Post
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            {post.platform_post_id}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(post.posted_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: "#f8fafc",
                    borderRadius: 1.5,
                    border: "1px dashed #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    Not posted to any social platform
                  </Typography>
                </Box>
              )}

              {/* Configured platforms (from feed) */}
              {feedItemToDelete.display_platforms?.length > 0 && (
                <Box mb={2}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    display="block"
                    mb={0.5}
                  >
                    Configured Platforms
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {feedItemToDelete.display_platforms.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, textTransform: "capitalize" }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Partner notification preview (if partner feed) */}
              {selectedFeedForItems?.feed_type === FeedType.PARTNER && (
                <Alert severity="info" sx={{ borderRadius: 1.5, mt: 1 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Partner Notification
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    A notification email will be sent to the partner when email
                    sending is enabled.
                  </Typography>
                </Alert>
              )}

              <Alert severity="warning" sx={{ mt: 2, borderRadius: 1.5 }}>
                This action will remove the video from the feed. Social media
                posts will <strong>not</strong> be deleted from the platforms.
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#fff5f5",
            borderTop: "1px solid #fca5a5",
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDeleteItemDialog}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteItem}
            variant="contained"
            color="error"
            disabled={deletingItem}
            startIcon={deletingItem ? <HourglassEmptyIcon /> : <DeleteIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 700,
              background: "linear-gradient(135deg, #7f1d1d, #ef4444)",
            }}
          >
            {deletingItem ? "Deleting…" : "Delete Item"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────────── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 38, height: 38 }}
            >
              <WarningAmberIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Delete Feed
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }}>
            All feed items, publish history, and scheduler settings will be{" "}
            <strong>permanently deleted</strong>.
          </Alert>

          {feedToDelete && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                borderColor: "#fca5a5",
                bgcolor: "#fff5f5",
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Feed Name
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {feedToDelete.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Owner
                  </Typography>
                  <Typography variant="body2">
                    {feedToDelete.owner_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Title
                  </Typography>
                  <Typography variant="body2">{feedToDelete.title}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Published Items
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="error.main"
                  >
                    {feedToDelete.items_count ?? 0} items will be lost
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    XML URL
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      color: "#7f1d1d",
                    }}
                  >
                    /mrss/feed/{feedToDelete.xml_url}/
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Box
            sx={{
              p: 2,
              bgcolor: "#fef3c7",
              borderRadius: 1.5,
              border: "1px solid #fde68a",
              mb: 1.5,
            }}
          >
            <Typography variant="body2" fontWeight={600} color="#92400e">
              Type{" "}
              <code
                style={{
                  background: "#fde68a",
                  padding: "1px 5px",
                  borderRadius: 3,
                }}
              >
                delete
              </code>{" "}
              to confirm:
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Type delete to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            error={
              deleteConfirmText.length > 0 && deleteConfirmText !== "delete"
            }
            helperText={
              deleteConfirmText.length > 0 && deleteConfirmText !== "delete"
                ? 'Must be exactly "delete"'
                : " "
            }
            autoComplete="off"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#fff5f5",
            borderTop: "1px solid #fca5a5",
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== "delete"}
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 700,
              background:
                deleteConfirmText === "delete"
                  ? "linear-gradient(135deg, #7f1d1d, #ef4444)"
                  : undefined,
            }}
          >
            Delete Feed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FeedsPreview;
